function trimTrailingSlash(value) {
  return String(value || '').replace(/\/+$/, '');
}

function createId(prefix) {
  const randomPart = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return `${prefix}_${randomPart}`;
}

function getStoredId(storage, key, prefix) {
  if (!storage) {
    return createId(prefix);
  }

  try {
    const existing = storage.getItem(key);
    if (existing) {
      return existing;
    }

    const id = createId(prefix);
    storage.setItem(key, id);
    return id;
  } catch (_error) {
    return createId(prefix);
  }
}

function getSource(location, referrer = '') {
  if (!location) {
    return null;
  }

  const params = new URLSearchParams(location.search || '');
  return params.get('utm_source') || params.get('ref') || referrer || location.referrer || 'direct';
}

function sanitizeMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return {};
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (Object.keys(sanitized).length >= 20) {
      break;
    }
    if (!/^[a-zA-Z0-9_.-]{1,64}$/.test(key)) {
      continue;
    }
    if (/authorization|cookie|password|secret|token/i.test(key)) {
      continue;
    }
    if (value === null || typeof value === 'boolean') {
      sanitized[key] = value;
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      sanitized[key] = value;
    } else if (typeof value === 'string') {
      sanitized[key] = value.slice(0, 256);
    }
  }

  return sanitized;
}

export function createFunnelTracker({
  apiBaseUrl,
  appSlug,
  apiKey,
  bearerToken,
  appType = 'structured_report',
  fetchImpl = fetch,
  storage = typeof localStorage !== 'undefined' ? localStorage : null,
  location = typeof window !== 'undefined' ? window.location : null,
  referrer = typeof document !== 'undefined' ? document.referrer : '',
} = {}) {
  const anonymousId = getStoredId(storage, `promptaas:${appSlug}:anonymous_id`, 'visitor');
  const sessionId = getStoredId(storage, `promptaas:${appSlug}:session_id`, 'session');

  return {
    async track(eventName, options = {}) {
      if (!apiBaseUrl || !appSlug || !eventName || (!bearerToken && !apiKey)) {
        return;
      }

      const headers = {
        'Content-Type': 'application/json',
      };
      if (bearerToken) {
        headers.Authorization = bearerToken;
      } else {
        headers['X-API-Key'] = apiKey;
      }

      const body = {
        event_name: eventName,
        session_id: sessionId,
        anonymous_id: anonymousId,
        user_id: options.userId || null,
        source: options.source || getSource(location, referrer),
        metadata: {
          app_type: appType,
          path: location?.pathname || '/',
          ...sanitizeMetadata(options.metadata),
        },
      };

      try {
        await fetchImpl(`${trimTrailingSlash(apiBaseUrl)}/app/${appSlug}/analytics/funnel-events`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          keepalive: true,
        });
      } catch (_error) {
        // Funnel telemetry must never block the user flow.
      }
    },
  };
}
